import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface ServiceStatus {
  name: string;
  status: 'operational' | 'degraded' | 'down';
  uptime: string;
  responseTime: string;
  icon: string;
}

const Status: React.FC = () => {
  const [services] = useState<ServiceStatus[]>([
    { name: 'Website', status: 'operational', uptime: '99.98%', responseTime: '120ms', icon: '🌐' },
    { name: 'API Services', status: 'operational', uptime: '99.95%', responseTime: '85ms', icon: '⚡' },
    { name: 'Database', status: 'operational', uptime: '99.99%', responseTime: '45ms', icon: '🗄️' },
    { name: 'CDN', status: 'operational', uptime: '99.97%', responseTime: '35ms', icon: '🚀' },
    { name: 'Email Service', status: 'operational', uptime: '99.92%', responseTime: '200ms', icon: '📧' },
    { name: 'Payment Gateway', status: 'operational', uptime: '99.96%', responseTime: '150ms', icon: '💳' },
  ]);

  const [overallStatus, setOverallStatus] = useState<'operational' | 'degraded' | 'down'>('operational');

  useEffect(() => {
    // Calculate overall status
    const hasDown = services.some(s => s.status === 'down');
    const hasDegraded = services.some(s => s.status === 'degraded');

    if (hasDown) {
      setOverallStatus('down');
    } else if (hasDegraded) {
      setOverallStatus('degraded');
    } else {
      setOverallStatus('operational');
    }
  }, [services]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)', text: '#22c55e', dot: '#22c55e' };
      case 'degraded':
        return { bg: 'rgba(251, 191, 36, 0.1)', border: 'rgba(251, 191, 36, 0.3)', text: '#fbbf24', dot: '#fbbf24' };
      case 'down':
        return { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', text: '#ef4444', dot: '#ef4444' };
      default:
        return { bg: 'rgba(156, 163, 175, 0.1)', border: 'rgba(156, 163, 175, 0.3)', text: '#9ca3af', dot: '#9ca3af' };
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'operational':
        return 'Operational';
      case 'degraded':
        return 'Degraded Performance';
      case 'down':
        return 'Down';
      default:
        return 'Unknown';
    }
  };

  const overallStatusColors = getStatusColor(overallStatus);

  return (
    <section className="py-20 relative overflow-hidden min-h-screen">
      {/* Background gradient */}
      <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.2), transparent 70%)', filter: 'blur(60px)' }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span
              style={{
                background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              System Status
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Real-time status and performance metrics
          </p>
        </motion.div>

        {/* Overall Status */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <div
            className="p-8 rounded-2xl text-center"
            style={{
              background: overallStatusColors.bg,
              border: `1px solid ${overallStatusColors.border}`,
              backdropFilter: 'blur(10px)'
            }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div
                className="w-4 h-4 rounded-full animate-pulse"
                style={{ backgroundColor: overallStatusColors.dot }}
              ></div>
              <h2 className="text-3xl font-bold" style={{ color: overallStatusColors.text }}>
                All Systems {getStatusText(overallStatus)}
              </h2>
            </div>
            <p className="text-gray-400">
              Last updated: {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          </div>
        </motion.div>

        {/* Services Status */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service, index) => {
            const statusColors = getStatusColor(service.status);
            return (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.05 }}
                className="glass p-6 rounded-2xl"
                style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{service.icon}</span>
                    <h3 className="text-lg font-semibold text-white">{service.name}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: statusColors.dot }}
                    ></div>
                    <span className="text-sm font-medium" style={{ color: statusColors.text }}>
                      {getStatusText(service.status)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Uptime (30 days)</span>
                    <span className="text-white font-medium">{service.uptime}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Avg Response Time</span>
                    <span className="text-white font-medium">{service.responseTime}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Uptime History */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="glass p-8 rounded-2xl"
          style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          <h2 className="text-2xl font-bold text-white mb-6">90-Day Uptime History</h2>

          <div className="flex items-end gap-1 h-32 mb-6">
            {Array.from({ length: 90 }).map((_, i) => {
              const height = 60 + Math.random() * 40; // Random height between 60-100%
              const isDown = Math.random() > 0.98; // 2% chance of downtime
              return (
                <div
                  key={i}
                  className="flex-1 rounded-t transition-all hover:opacity-70 cursor-pointer"
                  style={{
                    height: `${height}%`,
                    backgroundColor: isDown ? '#ef4444' : '#22c55e',
                    opacity: 0.7
                  }}
                  title={`Day ${i + 1}: ${isDown ? 'Some downtime' : 'Operational'}`}
                ></div>
              );
            })}
          </div>

          <div className="flex items-center gap-6 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#22c55e' }}></div>
              <span>Operational</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#fbbf24' }}></div>
              <span>Degraded</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#ef4444' }}></div>
              <span>Down</span>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Status;
